import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import { subPeriod, toDateString } from "helpers";
import { Jobs } from "models/Job";
import {
  GetCustomerByMachineQueryVariables,
  GetCustomerQueryVariables,
  GetCustomersQueryVariables,
} from "operations/schema/schema";
import { AppAsyncThunkConfig } from "store";
import { State } from "./history.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncQueries = {
  getHistoryJob: createAppAsyncThunk(
    "history/getHistoryJob",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { root } = getState();
      const selectedJobId = root.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getJob, {
        variables: {
          id: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.job) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getTechnicianHistory: createAppAsyncThunk(
    "history/getTechnicianHistory",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const filter = getState().history.historyFilter;
      const { data, errors } = await graphqlRequest(sdk.getTechnicianHistory, {
        variables: {
          from: toDateString(subPeriod(filter.typeDate)),
          specificDate: toDateString(filter.specificDate),
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.technicianHistory) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  getServiceHistory: createAppAsyncThunk(
    "history/getServiceHistory",
    async (
      { type, typeId }: { type?: string; typeId?: string },
      { getState, rejectWithValue, extra: { sdk } },
    ) => {
      const filter = getState().history.serviceHistoryFilter;
      const { data, errors } = await graphqlRequest(sdk.getServiceHistory, {
        variables: {
          machineId: type === "machine" ? typeId : null,
          fromDate: toDateString(subPeriod(filter.typeDate)),
          specificDate: toDateString(filter.specificDate),
          customerId: type === "machine" ? null : typeId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.serviceHistory) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  getSearchHistory: createAppAsyncThunk(
    "history/getSearchHistory",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const filter = getState().history.searchHistoryFilter;
      const { data, errors } = await graphqlRequest(sdk.getServiceHistory, {
        variables: {
          machineId: filter.machine ? filter.machine?.id : null,
          fromDate: toDateString(subPeriod(filter.typeDate)),
          specificDate: toDateString(filter.specificDate),
          customerId: filter.machine ? null : filter.customer?.id,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.serviceHistory) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  getFiles: createAppAsyncThunk(
    "history/getFiles",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { root } = getState();
      const selectedJobId = root.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getFiles, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.files) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getNotes: createAppAsyncThunk(
    "history/getNotes",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { root } = getState();
      const selectedJobId = root.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getWorkNotes, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.workNotes) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getVisits: createAppAsyncThunk(
    "history/getVisits",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const selectedJobId = getState().root.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getJobVisitsHistory, {
        variables: {
          max: 20,
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.jobVisitsHistory) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getComments: createAppAsyncThunk(
    "history/getComments",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { root } = getState();
      const selectedJobId = root.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getComments, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.comments) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getCustomer: createAppAsyncThunk(
    "history/getCustomer",
    async (variables: GetCustomerQueryVariables, { rejectWithValue, getState, extra: { sdk } }) => {
      const { history } = getState();
      let existingCustomer = history.customers.find((c) => c.name === variables.nameFilter);
      if (existingCustomer) return existingCustomer;
      const { data, errors } = await graphqlRequest(sdk.getCustomer, {
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.customer) return rejectWithValue("something went wrong");
      return data.customer;
    },
  ),
  getCustomerByMachine: createAppAsyncThunk(
    "history/getCustomerByMachine",
    async (
      variables: GetCustomerByMachineQueryVariables,
      { rejectWithValue, getState, extra: { sdk } },
    ) => {
      const { data, errors } = await graphqlRequest(sdk.getCustomerByMachine, {
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.customerByMachine) return rejectWithValue("something went wrong");
      return data.customerByMachine;
    },
  ),
  getCustomers: createAppAsyncThunk(
    "history/getCustomers",
    async (variables: GetCustomersQueryVariables, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getCustomers, { variables });
      if (errors) return rejectWithValue(errors);
      if (!data?.customers) return rejectWithValue("something went wrong");
      return data.customers;
    },
  ),
};

export const queryBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncQueries.getHistoryJob.pending, (state) => {
    state.loadingJob = true;
    return state;
  });
  builder.addCase(asyncQueries.getHistoryJob.rejected, (state) => {
    state.loadingJob = false;
    return state;
  });
  builder.addCase(
    asyncQueries.getHistoryJob.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      let { job } = data;
      if (!selectedJobId || !job) return state;
      /**
       * While this "if" shouldn't be necessary, selected job can be undefined
       * if you start the app or reload while viewing a job
       */
      if (!state.jobs[selectedJobId]) {
        state.jobs[selectedJobId] = {
          ...job,
          files: [],
          workNotes: [],
          visits: [],
          comments: [],
        };
      } else {
        state.jobs[selectedJobId] = {
          ...state.jobs[selectedJobId],
          ...job,
          files: state.jobs[selectedJobId].files || [],
          workNotes: state.jobs[selectedJobId].workNotes || [],
          visits: state.jobs[selectedJobId].visits || [],
          comments: state.jobs[selectedJobId].comments || [],
        };
      }
      state.loadingJob = false;
      return state;
    },
  );

  // Technician history
  builder.addCase(asyncQueries.getTechnicianHistory.pending, (state) => {
    state.loadingJobs = true;
    return state;
  });
  builder.addCase(asyncQueries.getTechnicianHistory.rejected, (state) => {
    state.loadingJobs = false;
    return state;
  });
  builder.addCase(asyncQueries.getTechnicianHistory.fulfilled, (state, { payload: data }) => {
    let serviceJobs = data.technicianHistory;
    let newJobs = serviceJobs.reduce((obj, next) => {
      obj[next.id] = {
        ...next,
        files: [],
        workNotes: [],
        visits: [],
        comments: [],
      };
      return obj;
    }, {} as Jobs);

    state.jobs = newJobs;
    state.loadingJobs = false;
    return state;
  });

  // Service history
  builder.addCase(asyncQueries.getServiceHistory.pending, (state) => {
    state.loadingServiceHistory = true;
    return state;
  });
  builder.addCase(asyncQueries.getServiceHistory.rejected, (state) => {
    state.loadingServiceHistory = false;
    return state;
  });
  builder.addCase(asyncQueries.getServiceHistory.fulfilled, (state, { payload: data }) => {
    state.serviceHistory = [...data.serviceHistory];
    state.loadingServiceHistory = false;
    return state;
  });

  // Search history
  builder.addCase(asyncQueries.getSearchHistory.pending, (state) => {
    state.loadingSearchHistory = true;
    return state;
  });
  builder.addCase(asyncQueries.getSearchHistory.rejected, (state) => {
    state.loadingSearchHistory = false;
    return state;
  });
  builder.addCase(asyncQueries.getSearchHistory.fulfilled, (state, { payload: data }) => {
    state.searchHistory = [...data.serviceHistory];
    state.loadingSearchHistory = false;
    return state;
  });

  builder.addCase(
    asyncQueries.getFiles.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId || !state.jobs[selectedJobId]) return state;
      state.jobs[selectedJobId].files = [...data.files];
      return state;
    },
  );
  builder.addCase(
    asyncQueries.getNotes.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId || !state.jobs[selectedJobId]) return state;
      state.jobs[selectedJobId].workNotes = [...data.workNotes];
      return state;
    },
  );
  builder.addCase(
    asyncQueries.getVisits.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId || !state.jobs[selectedJobId]) return state;
      state.jobs[selectedJobId].visits = [...data.jobVisitsHistory];
      return state;
    },
  );
  builder.addCase(asyncQueries.getComments.pending, (state) => {
    state.commentsLoading = true;
  });
  builder.addCase(asyncQueries.getComments.rejected, (state) => {
    state.commentsLoading = false;
  });
  builder.addCase(
    asyncQueries.getComments.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId || !state.jobs[selectedJobId]) return state;
      state.jobs[selectedJobId].comments = [...data.comments];
      state.commentsLoading = false;
      return state;
    },
  );
  builder.addCase(asyncQueries.getCustomer.pending, (state) => {
    state.customersLoading = true;
  });
  builder.addCase(asyncQueries.getCustomer.rejected, (state) => {
    state.customersLoading = false;
  });
  builder.addCase(asyncQueries.getCustomer.fulfilled, (state, { payload: customer }) => {
    state.customersLoading = false;
    const existingCustomer = state.customers.find((c) => c.id === customer.id);
    if (!existingCustomer) state.customers = [...state.customers, customer];
    return state;
  });
  builder.addCase(asyncQueries.getCustomerByMachine.pending, (state) => {
    state.customersLoading = true;
  });
  builder.addCase(asyncQueries.getCustomerByMachine.rejected, (state) => {
    state.customersLoading = false;
  });
  builder.addCase(asyncQueries.getCustomerByMachine.fulfilled, (state, { payload: customer }) => {
    state.customersLoading = false;
    const existingCustomer = state.customers.findIndex((c) => c.id === customer.id);
    if (existingCustomer === -1) state.customers = [...state.customers, customer];
    return state;
  });
  builder.addCase(asyncQueries.getCustomers.pending, (state) => {
    state.customersLoading = true;
  });
  builder.addCase(asyncQueries.getCustomers.rejected, (state) => {
    state.customersLoading = false;
  });
  builder.addCase(asyncQueries.getCustomers.fulfilled, (state, { payload: customers }) => {
    state.customersLoading = false;
    state.customers = [...customers];
    return state;
  });
};
